/**
 * BusinessChatPanel - Enhanced with AI SDK Elements
 *
 * Uses AI Elements components for better UX:
 * - Message, MessageAvatar, MessageContent for message display
 * - Tool, ToolHeader, ToolContent for tool invocations
 * - Suggestions for common queries
 * - Response for streaming markdown
 * - Loader for loading states
 */
import React, { useEffect, useRef } from 'react';
import { useAuth } from '@/components/auth/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Send, AlertCircle, MessageSquare, CheckCircle, XCircle, Sparkles, Bot, User, Paperclip, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { UiMessage, TextPart, ToolCallPart } from '@/chat/types';
import { parseSSEEventToParts, mergeParts } from '@/chat/stream-adapter';
import { addToolResult } from '@/chat/addToolResult';
import { Message, MessageAvatar, MessageContent } from '@/components/ai-elements/message';
import { Tool, ToolHeader, ToolContent } from '@/components/ai-elements/tool';
import { Suggestions, Suggestion } from '@/components/ai-elements/suggestion';
import { Response } from '@/components/ai-elements/response';
import { Loader } from '@/components/ai-elements/loader';
import { EditableProposalField, type EditableField } from '@/components/chat/EditableProposalField';

interface BusinessChatPanelProps {
  businessId: string;
  className?: string;
  onBusinessDataUpdate?: () => void | Promise<void>; // Callback to refresh business data after financial updates
}

/**
 * ProposalFieldDisplay - Component for rendering financial proposal fields
 * Now uses EditableProposalField for interactive editing with confidence scores
 */
interface ProposalFieldDisplayProps {
  year: string;
  editableFields: EditableField[];
  onFieldChange: (field: EditableField) => void;
}

const ProposalFieldDisplay: React.FC<ProposalFieldDisplayProps> = ({ year, editableFields, onFieldChange }) => {
  const fieldsForYear = editableFields.filter(f => f.year === year);

  if (fieldsForYear.length === 0) return null;

  return (
    <div className="rounded-lg border border-border/30 bg-muted/20 p-3 space-y-2">
      <div className="flex items-center gap-2">
        <div className="h-px flex-1 bg-border/40" />
        <span className="font-medium text-xs text-foreground/60 whitespace-nowrap px-2">
          {year}
        </span>
        <div className="h-px flex-1 bg-border/40" />
      </div>
      <div className="space-y-2.5">
        {fieldsForYear.map((field) => (
          <EditableProposalField
            key={`${field.year}-${field.fieldName}`}
            field={field}
            onChange={onFieldChange}
          />
        ))}
      </div>
    </div>
  );
};

export const BusinessChatPanel: React.FC<BusinessChatPanelProps> = ({
  businessId,
  className = '',
  onBusinessDataUpdate,
}) => {
  const { user, currentTeamId } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // SDK-compatible state with parts-based messages
  const [localInput, setLocalInput] = React.useState('');
  const [messages, setMessages] = React.useState<UiMessage[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);
  const [confirmingToolCall, setConfirmingToolCall] = React.useState<string | null>(null);
  const [attachedFile, setAttachedFile] = React.useState<File | null>(null);
  const [isReadingFile, setIsReadingFile] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Editable fields state: Map from toolCallId to edited fields
  const [editableFieldsMap, setEditableFieldsMap] = React.useState<Record<string, EditableField[]>>({});

  // PHASE 2: Store original tool call parameters for direct execution
  const [toolCallParamsMap, setToolCallParamsMap] = React.useState<Record<string, any>>({});

  // Helper to get text content from parts
  const getTextContent = (parts: Array<TextPart | ToolCallPart>): string => {
    return parts
      .filter((p): p is TextPart => p.type === 'text')
      .map((p) => p.text)
      .join('');
  };

  // Helper to get tool parts
  const getToolParts = (parts: Array<TextPart | ToolCallPart>): ToolCallPart[] => {
    return parts.filter((p): p is ToolCallPart => p.type !== 'text');
  };

  // Handle file selection
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    if (!validTypes.includes(file.type) && !file.name.match(/\.(csv|xls|xlsx)$/i)) {
      setError(new Error('Please upload a CSV or Excel file'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError(new Error('File size must be less than 5MB'));
      return;
    }

    setAttachedFile(file);
    setError(null);
  };

  // Remove attached file
  const handleRemoveFile = () => {
    setAttachedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Read file as text
  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  // Handle field changes from EditableProposalField
  const handleFieldChange = (toolCallId: string, updatedField: EditableField) => {
    setEditableFieldsMap(prev => {
      const fields = prev[toolCallId] || [];
      const updatedFields = fields.map(f =>
        f.year === updatedField.year && f.fieldName === updatedField.fieldName
          ? updatedField
          : f
      );
      return { ...prev, [toolCallId]: updatedFields };
    });
  };

  // Parse SSE stream into messages
  const parseStream = async (
    stream: ReadableStream<Uint8Array>,
    messageId: string
  ): Promise<void> => {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let hasFinancialUpdate = false;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const event of events) {
          if (!event.trim() || event === '[DONE]') continue;

          const dataMatch = event.match(/^data: (.+)$/m);
          if (!dataMatch) continue;

          try {
            const data = JSON.parse(dataMatch[1]);
            const newParts = parseSSEEventToParts(data);

            // Check if this is a successful confirmed financial update
            if (data.type === 'tool-result' &&
                data.toolName === 'updateFinancials' &&
                data.output?.applied === true) {
              hasFinancialUpdate = true;
            }

            // Check if this is a successful comparable addition (confirmed)
            if (data.type === 'tool-result' &&
                data.toolName === 'addComparable' &&
                data.output?.success === true &&
                !data.output?.proposalOnly) {
              hasFinancialUpdate = true; // Reuse the same refresh mechanism
            }

            // PHASE 2: Store tool call parameters for direct execution
            if (data.type === 'tool-call') {
              const toolCallId = data.toolCallId;
              setToolCallParamsMap(prev => ({
                ...prev,
                [toolCallId]: {
                  toolName: data.toolName,
                  input: data.input,
                }
              }));
            }

            // Initialize editable fields if this is a financial proposal with editableFields
            if (data.type === 'tool-result' &&
                data.toolName === 'updateFinancials' &&
                data.output?.editableFields &&
                data.output?.proposalOnly) {
              const toolCallId = data.toolCallId;
              setEditableFieldsMap(prev => ({
                ...prev,
                [toolCallId]: data.output.editableFields
              }));
            }

            if (newParts.length > 0) {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === messageId
                    ? {
                        ...msg,
                        parts: mergeParts(msg.parts, newParts),
                        status: 'streaming',
                      }
                    : msg
                )
              );
            }
          } catch (parseError) {
            console.error('[Stream Parse Error]:', parseError);
          }
        }
      }

      // Mark message as ready
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId ? { ...msg, status: 'ready' } : msg
        )
      );

      // Trigger business data refresh if financial update was applied
      if (hasFinancialUpdate && onBusinessDataUpdate) {
        console.log('[BusinessChatPanel] Financial update detected, refreshing business data...');
        await onBusinessDataUpdate();
      }
    } catch (err) {
      console.error('[Stream Error]:', err);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId
            ? { ...msg, status: 'error', error: err instanceof Error ? err.message : 'Stream error' }
            : msg
        )
      );
    }
  };

  // Handle confirming a comparable addition proposal
  const handleConfirmComparable = async (toolPart: ToolCallPart) => {
    if (!toolPart.result?.proposedComparables || confirmingToolCall) return;

    setConfirmingToolCall(toolPart.toolCallId);
    setError(null);
    setIsLoading(true);

    try {
      // PHASE 2: Direct Tool Execution
      // Get the original tool call parameters
      const storedToolCall = toolCallParamsMap[toolPart.toolCallId];

      if (storedToolCall && storedToolCall.toolName === 'addComparable') {
        // Use original params directly - no user editing for comparables (yet)
        const params = { ...storedToolCall.input };

        // Execute directly - skip AI interpretation
        const stream = await addToolResult({
          toolCallId: toolPart.toolCallId,
          result: {
            executeDirectly: true,
            toolName: 'addComparable',
            params,
          },
          conversation: messages,
          context: {
            businessId,
            teamId: currentTeamId,
            userId: user?.id,
          },
        });

        if (stream) {
          // Remove the proposal from the current message immediately
          setMessages((prev) =>
            prev.map((msg) => {
              const toolParts = msg.parts.filter((p): p is ToolCallPart => p.type !== 'text');
              const hasThisProposal = toolParts.some(t => t.toolCallId === toolPart.toolCallId);

              if (hasThisProposal) {
                return {
                  ...msg,
                  parts: msg.parts.filter(
                    (p) => p.type === 'text' || p.toolCallId !== toolPart.toolCallId
                  ),
                };
              }
              return msg;
            })
          );

          const assistantMessageId = `msg-${Date.now()}`;
          setMessages((prev) => [
            ...prev,
            {
              id: assistantMessageId,
              role: 'assistant',
              parts: [],
              status: 'streaming',
            },
          ]);

          await parseStream(stream, assistantMessageId);
        }
      } else {
        // Fallback: no stored params (shouldn't happen, but handle gracefully)
        console.warn('[handleConfirmComparable] No stored tool call params found, using fallback');
        throw new Error('Tool parameters not found. Please try again.');
      }
    } catch (err) {
      console.error('[Confirm Comparable Error]:', err);
      setError(err instanceof Error ? err : new Error('Failed to confirm comparable'));
    } finally {
      setIsLoading(false);
      setConfirmingToolCall(null);

      // Cleanup: Remove tool call params from map to prevent memory leak
      setToolCallParamsMap(prev => {
        const { [toolPart.toolCallId]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  // Handle confirming a financial update proposal using addToolResult
  const handleConfirmUpdate = async (toolPart: ToolCallPart) => {
    if (!toolPart.result?.proposedFinancials || confirmingToolCall) return;

    setConfirmingToolCall(toolPart.toolCallId);
    setError(null);
    setIsLoading(true);

    try {
      // PHASE 2: Direct Tool Execution
      // Get the original tool call parameters
      const storedToolCall = toolCallParamsMap[toolPart.toolCallId];

      if (storedToolCall && storedToolCall.toolName === 'updateFinancials') {
        // Get edited fields if user modified the proposal
        const editedFields = editableFieldsMap[toolPart.toolCallId] || [];

        // Start with original params
        let params = { ...storedToolCall.input };

        // If user edited values, update the years array
        if (editedFields.length > 0) {
          const editedYears = editedFields.reduce((acc, field) => {
            const existingYear = acc.find((y: any) => y.year === field.year);

            // Use inputValue directly - no transformation
            const valueForBackend = field.inputValue;

            if (existingYear) {
              existingYear[field.fieldName] = valueForBackend;
            } else {
              acc.push({
                year: field.year,
                [field.fieldName]: valueForBackend
              });
            }
            return acc;
          }, [] as any[]);

          params.years = editedYears;
        }

        // Execute directly - skip AI interpretation
        const stream = await addToolResult({
          toolCallId: toolPart.toolCallId,
          result: {
            executeDirectly: true,
            toolName: 'updateFinancials',
            params,
          },
          conversation: messages,
          context: {
            businessId,
            teamId: currentTeamId,
            userId: user?.id,
          },
        });

        if (stream) {
          // Remove the proposal from the current message immediately
          setMessages((prev) =>
            prev.map((msg) => {
              const toolParts = msg.parts.filter((p): p is ToolCallPart => p.type !== 'text');
              const hasThisProposal = toolParts.some(t => t.toolCallId === toolPart.toolCallId);

              if (hasThisProposal) {
                return {
                  ...msg,
                  parts: msg.parts.filter(
                    (p) => p.type === 'text' || p.toolCallId !== toolPart.toolCallId
                  ),
                };
              }
              return msg;
            })
          );

          const assistantMessageId = `msg-${Date.now()}`;
          setMessages((prev) => [
            ...prev,
            {
              id: assistantMessageId,
              role: 'assistant',
              parts: [],
              status: 'streaming',
            },
          ]);

          await parseStream(stream, assistantMessageId);
        }
      } else {
        // Fallback: no stored params (shouldn't happen, but handle gracefully)
        console.warn('[handleConfirmUpdate] No stored tool call params found, using fallback');
        throw new Error('Tool parameters not found. Please try again.');
      }
    } catch (err) {
      console.error('[Confirm Update Error]:', err);
      setError(err instanceof Error ? err : new Error('Failed to confirm update'));
    } finally {
      setIsLoading(false);
      setConfirmingToolCall(null);

      // Cleanup: Remove tool call params from map to prevent memory leak
      setToolCallParamsMap(prev => {
        const { [toolPart.toolCallId]: _, ...rest } = prev;
        return rest;
      });

      // Cleanup: Remove editable fields from map
      setEditableFieldsMap(prev => {
        const { [toolPart.toolCallId]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  // Handle submit with plain fetch
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!localInput.trim() && !attachedFile) || isLoading || isReadingFile) return;

    let fileContent: string | null = null;
    let fileName: string | null = null;

    // If file is attached, read and validate content
    if (attachedFile) {
      setIsReadingFile(true);
      try {
        const content = await readFileAsText(attachedFile);

        // Basic CSV validation
        const lines = content.trim().split('\n');
        if (lines.length < 2) {
          setError(new Error('CSV must have at least a header row and one data row'));
          setIsReadingFile(false);
          return;
        }

        // Validate content size (prevent token limit issues)
        if (content.length > 100000) {
          setError(new Error('File content too large to process. Please keep CSV files under 100KB of text.'));
          setIsReadingFile(false);
          return;
        }

        fileContent = content;
        fileName = attachedFile.name;
      } catch (err) {
        setError(new Error('Failed to read file'));
        setIsReadingFile(false);
        return;
      } finally {
        setIsReadingFile(false);
      }
    }

    // Create user message with just the text prompt and file metadata
    const displayText = localInput || (fileName ? 'Please parse this financial data' : '');
    const userMessage: UiMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      parts: [{
        type: 'text',
        text: displayText,
      }],
      status: 'submitted',
      // Store file metadata for display
      ...(fileName && { metadata: { fileName } }),
    };

    setMessages((prev) => [...prev, userMessage]);
    setLocalInput('');
    setAttachedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setIsLoading(true);
    setError(null);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      // Build the message content for the API (includes file content)
      const apiMessages = messages
        .concat(userMessage)
        .map((m) => {
          const textContent = getTextContent(m.parts);
          // For the current message with file, prepend file content
          if (m.id === userMessage.id && fileContent && fileName) {
            return {
              role: m.role,
              content: `[Uploaded file: ${fileName}]\n\n${fileContent}\n\n${textContent}`,
            };
          }
          return {
            role: m.role,
            content: textContent,
          };
        });

      const response = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token || ''}`,
        },
        body: JSON.stringify({
          messages: apiMessages,
          business_id: businessId,
          team_id: currentTeamId,
          user_id: user?.id,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      if (response.body) {
        const assistantMessageId = `msg-${Date.now() + 1}`;
        setMessages((prev) => [
          ...prev,
          {
            id: assistantMessageId,
            role: 'assistant',
            parts: [],
            status: 'streaming',
          },
        ]);

        await parseStream(response.body, assistantMessageId);
      }
    } catch (err) {
      console.error('[BusinessChatPanel] Error:', err);
      setError(err instanceof Error ? err : new Error('Failed to send message'));
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auth guard
  if (!user || !currentTeamId) {
    return (
      <div className={`flex flex-col items-center justify-center h-full p-8 ${className}`}>
        <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-sm text-muted-foreground">Please sign in to use chat</p>
      </div>
    );
  }

  const hasText = localInput && localInput.trim().length > 0;
  const hasFile = !!attachedFile;
  const isDisabled = isLoading || isReadingFile || (!hasText && !hasFile);

  return (
    <div className={`flex flex-col h-full bg-background ${className}`}>
      {/* Header - Refined minimalist design */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/40">
        <div className="flex items-center gap-2.5">
          <div className="relative">
            <div className="absolute inset-0 bg-purple-500/20 blur-md rounded-full" />
            <div className="relative rounded-lg bg-gradient-to-br from-purple-500/10 to-blue-500/10 p-1.5 border border-purple-500/20">
              <Sparkles className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-medium text-sm truncate text-foreground/90">AI Assistant</h3>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-6">
          {/* Empty state - Clean minimal design */}
          {messages.length === 0 && (
            <div className="flex flex-col h-full items-center justify-center px-6">
              <div className="flex flex-col items-center space-y-4 max-w-xs w-full">
                {/* Compact icon */}
                <div className="relative">
                  <div className="absolute inset-0 bg-purple-500/5 blur-xl rounded-full" />
                  <div className="relative rounded-xl bg-gradient-to-br from-purple-500/5 to-blue-500/5 p-3 border border-border/30">
                    <Sparkles className="h-5 w-5 text-purple-600/70 dark:text-purple-400/70" />
                  </div>
                </div>

                {/* Text content */}
                <p className="text-xs text-center text-muted-foreground/70 leading-relaxed">
                  Ask me anything about this business
                </p>

                {/* Compact suggestion buttons */}
                <div className="w-full space-y-1.5">
                  <button
                    type="button"
                    onClick={() => setLocalInput("What's the current valuation?")}
                    className="w-full text-left px-3 py-2 rounded-lg text-xs text-foreground/60 hover:text-foreground hover:bg-muted/40 transition-colors duration-150 border border-border/20 hover:border-border/40"
                    aria-label="Ask about current valuation"
                  >
                    What's the current valuation?
                  </button>
                  <button
                    type="button"
                    onClick={() => setLocalInput("Explain the SDE calculation")}
                    className="w-full text-left px-3 py-2 rounded-lg text-xs text-foreground/60 hover:text-foreground hover:bg-muted/40 transition-colors duration-150 border border-border/20 hover:border-border/40"
                    aria-label="Ask about SDE calculation"
                  >
                    Explain the SDE calculation
                  </button>
                  <button
                    type="button"
                    onClick={() => setLocalInput("What are the key risks?")}
                    className="w-full text-left px-3 py-2 rounded-lg text-xs text-foreground/60 hover:text-foreground hover:bg-muted/40 transition-colors duration-150 border border-border/20 hover:border-border/40"
                    aria-label="Ask about key risks"
                  >
                    What are the key risks?
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Message list */}
          {messages.map((message) => {
            const textContent = getTextContent(message.parts);
            const toolParts = getToolParts(message.parts);
            const isUser = message.role === 'user';

            return (
              <div key={message.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                {/* Content */}
                <div className={`flex flex-col space-y-2 min-w-0 max-w-[85%] ${isUser ? 'items-end' : 'items-start'}`}>
                  {/* Tool Parts - Show running tools at top without avatar/bubble */}
                  {toolParts.length > 0 && (() => {
                    const runningTools = toolParts.filter((tool) => tool.state === 'running');

                    return (
                      <>
                        {/* Running tools - subtle inline indicator */}
                        {runningTools.length > 0 && (
                          <div className="flex items-center gap-2 pl-1">
                            <Loader size={12} className="text-muted-foreground/60" />
                            <span className="text-xs text-muted-foreground/60 italic">
                              {runningTools[0].name === 'updateFinancials' ? 'Preparing financial update...' :
                               runningTools[0].name === 'getBusinessContext' ? 'Looking up business...' :
                               runningTools[0].name === 'addComparable' ? 'Adding comparable sale...' :
                               'Working on it...'}
                            </span>
                          </div>
                        )}
                      </>
                    );
                  })()}

                  {/* File Attachment Badge (for user messages) */}
                  {isUser && message.metadata?.fileName && (
                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted/60 border border-border/40 text-xs">
                      <Paperclip className="h-3 w-3 text-muted-foreground" />
                      <span className="font-medium text-foreground/80 truncate max-w-[200px]">
                        {message.metadata.fileName}
                      </span>
                    </div>
                  )}

                  {/* Text Content */}
                  {textContent && (
                    <div className={`rounded-2xl px-4 py-2.5 ${
                      isUser
                        ? 'bg-foreground text-background'
                        : 'bg-muted/50 border border-border/40 text-foreground'
                    }`}>
                      {message.role === 'assistant' ? (
                        <div className="prose prose-sm max-w-none dark:prose-invert prose-p:leading-relaxed prose-p:text-sm prose-p:my-1 break-words">
                          <Response>{textContent}</Response>
                        </div>
                      ) : (
                        <div className="text-sm leading-relaxed break-words">
                          {textContent}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tool Results */}
                  {toolParts.length > 0 && (() => {
                    // Group proposals by type
                    const financialProposals = toolParts.filter(
                      (tool) => tool.state === 'result' &&
                                tool.name === 'updateFinancials' &&
                                tool.result?.proposalOnly
                    );
                    const comparableProposals = toolParts.filter(
                      (tool) => tool.state === 'result' &&
                                tool.name === 'addComparable' &&
                                tool.result?.proposalOnly
                    );
                    const otherTools = toolParts.filter(
                      (tool) => !(tool.state === 'result' &&
                                  ((tool.name === 'updateFinancials' && tool.result?.proposalOnly) ||
                                   (tool.name === 'addComparable' && tool.result?.proposalOnly)))
                    );

                    return (
                      <div className="w-full space-y-2">
                        {/* Consolidated Financial Update Proposals */}
                        {financialProposals.length > 0 && (
                          <div className="w-full rounded-xl border border-border/40 bg-background overflow-hidden shadow-sm">
                            <div className="px-4 py-3 border-b border-border/30 bg-muted/20">
                              <div className="flex items-center gap-2.5">
                                <div className="relative">
                                  <div className="absolute inset-0 bg-purple-500/20 blur-sm rounded-full" />
                                  <div className="relative h-6 w-6 rounded-full bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/20 flex items-center justify-center">
                                    <CheckCircle className="h-3 w-3 text-purple-600 dark:text-purple-400" />
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-sm font-medium text-foreground/90">Proposed Changes</h4>
                                  <p className="text-xs text-muted-foreground/70">Review before applying</p>
                                </div>
                              </div>
                            </div>
                            <div className="p-3 space-y-2">
                              {/* Render editable fields for each year */}
                              {(() => {
                                // Get editable fields from the first proposal (they're all the same)
                                const toolCallId = financialProposals[0].toolCallId;
                                const editableFields = editableFieldsMap[toolCallId] || [];

                                // Get unique years from editable fields
                                const years = [...new Set(editableFields.map(f => f.year))].sort();

                                return years.map((year) => (
                                  <ProposalFieldDisplay
                                    key={year}
                                    year={year}
                                    editableFields={editableFields}
                                    onFieldChange={(field) => handleFieldChange(toolCallId, field)}
                                  />
                                ));
                              })()}
                              <div className="flex gap-2 pt-3 px-3 pb-3">
                                <Button
                                  onClick={() => handleConfirmUpdate(financialProposals[0])}
                                  disabled={!!confirmingToolCall}
                                  size="sm"
                                  className="flex-1 bg-foreground text-background hover:bg-foreground/90"
                                >
                                  {confirmingToolCall === financialProposals[0].toolCallId ? (
                                    <>
                                      <Loader size={12} className="mr-1.5" />
                                      Applying
                                    </>
                                  ) : (
                                    'Apply'
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={!!confirmingToolCall}
                                  className="hover:bg-muted"
                                  onClick={() => {
                                    // Remove all financial proposal tool parts from message
                                    setMessages((prev) =>
                                      prev.map((msg) =>
                                        msg.id === message.id
                                          ? {
                                              ...msg,
                                              parts: msg.parts.filter(
                                                (p) =>
                                                  p.type === 'text' ||
                                                  !financialProposals.some(fp => fp.toolCallId === p.toolCallId)
                                              ),
                                            }
                                          : msg
                                      )
                                    );
                                  }}
                                >
                                  Dismiss
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Comparable Sales Proposals */}
                        {comparableProposals.length > 0 && (
                          <div className="w-full rounded-xl border border-border/40 bg-background overflow-hidden shadow-sm">
                            <div className="px-4 py-3 border-b border-border/30 bg-muted/20">
                              <div className="flex items-center gap-2.5">
                                <div className="relative">
                                  <div className="absolute inset-0 bg-purple-500/20 blur-sm rounded-full" />
                                  <div className="relative h-6 w-6 rounded-full bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/20 flex items-center justify-center">
                                    <CheckCircle className="h-3 w-3 text-purple-600 dark:text-purple-400" />
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-sm font-medium text-foreground/90">Proposed Comparable Sale{comparableProposals[0].result.proposedComparables?.length > 1 ? 's' : ''}</h4>
                                  <p className="text-xs text-muted-foreground/70">Review before adding</p>
                                </div>
                              </div>
                            </div>
                            <div className="p-4 space-y-3">
                              {comparableProposals[0].result.proposedComparables?.map((comp: any, idx: number) => (
                                <div key={idx} className="rounded-lg border border-border/30 bg-muted/20 p-3">
                                  <div className="space-y-2">
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <h5 className="font-medium text-sm text-foreground/90">{comp.businessName}</h5>
                                        {comp.source && (
                                          <p className="text-xs text-muted-foreground/60 mt-0.5">{comp.source}</p>
                                        )}
                                      </div>
                                      {comp.salePrice && (
                                        <div className="text-right">
                                          <p className="font-semibold text-sm text-foreground">${(comp.salePrice / 1000000).toFixed(2)}M</p>
                                        </div>
                                      )}
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                      {comp.saleDate && (
                                        <div>
                                          <span className="text-muted-foreground/60">Sale Date:</span>
                                          <span className="ml-1 text-foreground/80">{comp.saleDate}</span>
                                        </div>
                                      )}
                                      {comp.revenueMultiple && (
                                        <div>
                                          <span className="text-muted-foreground/60">Revenue Multiple:</span>
                                          <span className="ml-1 text-foreground/80">{comp.revenueMultiple.toFixed(2)}x</span>
                                        </div>
                                      )}
                                      {comp.sdeMultiple && (
                                        <div>
                                          <span className="text-muted-foreground/60">SDE Multiple:</span>
                                          <span className="ml-1 text-foreground/80">{comp.sdeMultiple.toFixed(2)}x</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                              <div className="flex gap-2 pt-2">
                                <Button
                                  onClick={() => handleConfirmComparable(comparableProposals[0])}
                                  disabled={!!confirmingToolCall}
                                  size="sm"
                                  className="flex-1 bg-foreground text-background hover:bg-foreground/90"
                                >
                                  {confirmingToolCall === comparableProposals[0].toolCallId ? (
                                    <>
                                      <Loader size={12} className="mr-1.5" />
                                      Adding
                                    </>
                                  ) : (
                                    'Add to Comparables'
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={!!confirmingToolCall}
                                  className="hover:bg-muted"
                                  onClick={() => {
                                    // Remove all comparable proposal tool parts from message
                                    setMessages((prev) =>
                                      prev.map((msg) =>
                                        msg.id === message.id
                                          ? {
                                              ...msg,
                                              parts: msg.parts.filter(
                                                (p) =>
                                                  p.type === 'text' ||
                                                  !comparableProposals.some(cp => cp.toolCallId === p.toolCallId)
                                              ),
                                            }
                                          : msg
                                      )
                                    );
                                  }}
                                >
                                  Dismiss
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Other tool parts - results only, running tools shown above */}
                        {otherTools.filter(t => t.state !== 'running').map((tool) => (
                          <div key={tool.toolCallId} className="text-sm">
                            {/* Tool Result - Financial Update Applied */}
                            {tool.state === 'result' &&
                              tool.name === 'updateFinancials' &&
                              tool.result?.applied && (
                                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-500/5 border border-green-500/20 text-green-700 dark:text-green-400">
                                  <CheckCircle className="h-3.5 w-3.5" />
                                  <span className="text-xs font-medium">
                                    Changes applied
                                  </span>
                                </div>
                              )}

                            {/* Tool Result - Add Comparable (confirmed only, not proposals) */}
                            {tool.state === 'result' &&
                              tool.name === 'addComparable' &&
                              tool.result?.success &&
                              !tool.result?.proposalOnly && (
                                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-500/5 border border-green-500/20 text-green-700 dark:text-green-400">
                                  <CheckCircle className="h-3.5 w-3.5" />
                                  <span className="text-xs font-medium">
                                    Added {tool.result.comparablesAdded} comparable{tool.result.comparablesAdded !== 1 ? 's' : ''}
                                  </span>
                                </div>
                              )}

                            {/* Tool Result - Get Business Context */}
                            {tool.state === 'result' &&
                              tool.name === 'getBusinessContext' &&
                              tool.result?.success && (
                                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/30 border border-border/30 text-muted-foreground/80">
                                  <CheckCircle className="h-3 w-3" />
                                  <span className="text-xs">Found: {tool.result.business?.business_name}</span>
                                </div>
                              )}

                            {/* Tool Error */}
                            {tool.state === 'result' && tool.result?.success === false && (
                              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/5 border border-red-500/20 text-red-600 dark:text-red-400">
                                <XCircle className="h-3 w-3" />
                                <span className="text-xs">{tool.result.error || 'Failed'}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            );
          })}

          {/* Loading indicator */}
          {isLoading && (
            <div className="flex justify-start">
              <div className="flex items-center gap-2 px-3 py-2 rounded-2xl bg-muted/50 border border-border/40">
                <Loader size={12} className="text-muted-foreground/60" />
                <span className="text-xs text-muted-foreground/70">Thinking...</span>
              </div>
            </div>
          )}

          {/* Error display */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error.message || 'An error occurred'}</AlertDescription>
            </Alert>
          )}

          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input Area - Enhanced design */}
      <div className="p-3 border-t border-border/30">
        <form onSubmit={handleSubmit} className="relative">
          <div className="relative rounded-xl border border-border bg-muted/50 focus-within:border-border focus-within:bg-muted/30 transition-all shadow-sm">
            {/* File Preview Badge */}
            {attachedFile && (
              <div className="absolute -top-9 left-0 flex items-center gap-2 px-2.5 py-1.5 bg-muted/80 backdrop-blur-sm rounded-md text-xs border border-border/40 shadow-sm">
                <Paperclip className="h-3 w-3 text-muted-foreground" />
                <span className="font-medium truncate max-w-[200px]">{attachedFile.name}</span>
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  className="hover:bg-background/80 rounded p-0.5 transition-colors"
                  aria-label="Remove file"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}

            <Textarea
              value={localInput}
              onChange={(e) => setLocalInput(e.target.value)}
              placeholder="Ask a question..."
              disabled={isLoading}
              rows={1}
              className="resize-none border-0 bg-transparent focus-visible:ring-0 text-sm placeholder:text-muted-foreground/50 pr-20 py-3 px-3.5 min-h-[44px] w-full"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (!isDisabled) {
                    handleSubmit(e);
                  }
                }
              }}
            />

            {/* File Upload Button */}
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading}
              className="absolute right-10 bottom-1.5 h-8 w-8 rounded-lg hover:bg-background/80 disabled:opacity-30 transition-colors"
              aria-label="Attach file"
            >
              <Paperclip className="h-3.5 w-3.5" />
            </Button>

            {/* Send Button */}
            <Button
              type="submit"
              disabled={isDisabled}
              size="icon"
              variant="ghost"
              className="absolute right-1.5 bottom-1.5 h-8 w-8 rounded-lg hover:bg-background/80 disabled:opacity-30 transition-colors"
            >
              {isLoading || isReadingFile ? (
                <Loader size={16} />
              ) : (
                <Send className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>

          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleFileSelect}
            className="hidden"
          />
        </form>
      </div>
    </div>
  );
};

export default BusinessChatPanel;
